
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Play, Eye } from 'lucide-react';
import { type StartMarkerNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface StartMarkerNodeProps {
    data: StartMarkerNodeData & {
        // Replay mode props (optional)
        isReplayMode?: boolean;
        onReplayNodeClick?: (nodeId: string, mapNodeId?: string) => void;
    };
    selected?: boolean;
}

/**
 * Start Marker Node - UI helper showing the StartAt pointer.
 * 
 * This is NOT an ASL state - it's a visual indicator showing which state
 * is the entry point (ASL's StartAt property).
 * 
 * In replay mode, clicking the Start marker opens the inspector for
 * the first state (targetStateName).
 */
export const StartMarkerNode = memo(function StartMarkerNode({
    data,
    selected,
}: StartMarkerNodeProps) {
    const { 
        targetStateName, 
        layoutDirection = 'LR',
        isReplayMode,
        onReplayNodeClick,
    } = data;

    // Determine handle position based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;

    // Handle click in replay mode - open inspector for the first state
    const handleClick = () => {
        if (isReplayMode && onReplayNodeClick && targetStateName) {
            onReplayNodeClick(targetStateName);
        }
    };

    return (
        <div
            onClick={handleClick}
            className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-full',
                'bg-gradient-to-r from-green-500 to-emerald-500',
                'text-white font-medium text-sm',
                'shadow-lg',
                selected && 'ring-2 ring-blue-500 ring-offset-2',
                // Replay mode styling
                isReplayMode && 'cursor-pointer hover:shadow-xl transition-shadow',
                isReplayMode && 'hover:from-green-600 hover:to-emerald-600'
            )}
        >
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                {isReplayMode ? (
                    <Eye size={14} className="text-white" />
                ) : (
                    <Play size={14} className="text-white ml-0.5" />
                )}
            </div>
            <span>{isReplayMode ? 'View Start' : 'Start'}</span>

            {/* Output Handle - position based on layout direction */}
            <Handle
                type="source"
                id="source"
                position={outputPosition}
                className="!h-3 !w-3 !border-green-600 !bg-green-400"
            />
        </div>
    );
});
